import { useState } from 'react';
import { motion } from 'framer-motion';
import { Coins, Search } from 'lucide-react';
import NetworkBackground from '@/components/clusterscope/NetworkBackground';
import ClusterTopNav from '@/components/clusterscope/ClusterTopNav';
import type { Dataset } from '@/data/clusterscope-data';

const easeOut: [number, number, number, number] = [0.16, 1, 0.3, 1];

interface TokenScannerScreenProps {
  dataset: Dataset;
  searchValue: string;
  onSearchChange: (v: string) => void;
  onSearchSubmit: () => void;
  onNewSearch: () => void;
  onSwitchMode: (mode: 'token' | 'wallet') => void;
  onScanToken: (label: string) => void;
}

export default function TokenScannerScreen({
  dataset,
  searchValue,
  onSearchChange,
  onSearchSubmit,
  onNewSearch,
  onSwitchMode,
  onScanToken,
}: TokenScannerScreenProps) {
  const [tokenValue, setTokenValue] = useState('');

  return (
    <div className="relative flex h-screen w-full flex-col overflow-hidden bg-background">
      <ClusterTopNav
        dataset={dataset}
        mode="token"
        onModeChange={onSwitchMode}
        searchValue={searchValue}
        onSearchChange={onSearchChange}
        onSearchSubmit={onSearchSubmit}
        onNewSearch={onNewSearch}
      />

      <div className="pointer-events-none absolute inset-0 bg-grid-pattern opacity-[0.3]" />
      <NetworkBackground density={30} />
      <div className="pointer-events-none absolute left-1/4 top-0 h-[440px] w-[440px] rounded-full bg-[#a78bfa] opacity-[0.1] blur-[130px]" />
      <div className="pointer-events-none absolute right-1/4 bottom-0 h-[440px] w-[440px] rounded-full bg-[#14e0b0] opacity-[0.1] blur-[130px]" />

      <div className="relative z-10 flex flex-1 flex-col items-center justify-center overflow-y-auto px-4 py-10 sm:py-14">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.1, ease: easeOut }}
          className="glass group relative w-full max-w-md rounded-2xl border border-white/[0.08] bg-white/[0.03] p-7 backdrop-blur-xl transition-shadow duration-300 hover:border-[#a78bfa]/40 hover:shadow-[0_12px_50px_-12px_rgba(167,139,250,0.35)]"
        >
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#a78bfa]/15 text-[#a78bfa]">
              <Coins className="h-4.5 w-4.5" />
            </span>
            <span className="rounded-full border border-[#a78bfa]/40 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.15em] text-[#a78bfa]">
              Token Scanner
            </span>
          </div>

          <h2 className="text-xl font-bold text-foreground">Map Cluster Activity</h2>
          <p className="mt-2.5 text-sm leading-relaxed text-muted-foreground">
            Paste a token mint to reveal coordinated wallet clusters, timing correlations, and
            wash-trading rings operating on the token.
          </p>

          <div className="mt-5 flex items-stretch gap-2">
            <input
              value={tokenValue}
              onChange={(e) => setTokenValue(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && onScanToken(tokenValue.trim() || dataset.tryTokens[0])}
              placeholder="Token mint or ticker..."
              className="flex-1 rounded-lg border border-white/10 bg-black/30 px-3.5 py-2.5 text-sm text-foreground placeholder:text-muted-foreground/60 outline-none focus:border-[#a78bfa]/50"
            />
            <button
              onClick={() => onScanToken(tokenValue.trim() || dataset.tryTokens[0])}
              className="inline-flex items-center gap-1.5 rounded-lg bg-[#a78bfa] px-4 py-2.5 text-sm font-bold text-[#160f2b] transition-transform hover:scale-[1.03] active:scale-95"
            >
              <Search className="h-3.5 w-3.5" />
              Scan
            </button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {dataset.tryTokens.map((t) => (
              <button
                key={t}
                onClick={() => onScanToken(t)}
                className="rounded-full border border-white/10 px-3 py-1 text-xs text-muted-foreground transition-colors hover:border-[#a78bfa]/50 hover:text-foreground"
              >
                Try {t}
              </button>
            ))}
          </div>
        </motion.div>
      </div>
    </div>
  );
}
